/**
 * Role utility functions for checking user role access.
 * Implements hierarchical role-based access control.
 *
 * Roles (in order of increasing privilege):
 * - user: Base user role
 * - support: Support staff (can access Content, Feedback, Users, Invite Keys)
 * - admin: Administrators (can access Content, Feedback, Users, Invite Keys, Logs)
 * - platform_owner: Platform owners (can access all sections including Settings)
 */

export type UserRole = 'user' | 'support' | 'admin' | 'platform_owner'

export type AdminSection = 'content' | 'feedback' | 'users' | 'inviteKeys' | 'logs' | 'settings'

/**
 * Check if user has support+ access (support, admin, or platform_owner).
 */
export function hasSupportAccess(userRole: string | null | undefined): boolean {
    if (!userRole) return false
    return userRole === 'support' || userRole === 'admin' || userRole === 'platform_owner'
}

/**
 * Check if user has admin+ access (admin or platform_owner).
 */
export function hasAdminAccess(userRole: string | null | undefined): boolean {
    if (!userRole) return false
    return userRole === 'admin' || userRole === 'platform_owner'
}

/**
 * Check if user is platform owner.
 */
export function isPlatformOwner(userRole: string | null | undefined): boolean {
    if (!userRole) return false
    return userRole === 'platform_owner'
}

/**
 * Check if user can access a specific admin section.
 */
export function canAccessSection(
    userRole: string | null | undefined,
    section: AdminSection
): boolean {
    if (!userRole) return false

    switch (section) {
        case 'content':
        case 'feedback':
        case 'users':
        case 'inviteKeys':
            // Support+ can access
            return hasSupportAccess(userRole)
        case 'logs':
            // Admin+ can access
            return hasAdminAccess(userRole)
        case 'settings':
            // Platform owner only
            return isPlatformOwner(userRole)
        default:
            return false
    }
}

/**
 * Get the first accessible section for a user based on their role.
 * Returns the first section in the order: content, feedback, users, logs, settings
 * that the user can access.
 */
export function getFirstAccessibleSection(
    userRole: string | null | undefined
): AdminSection | null {
    if (!userRole) return null

    const sections: AdminSection[] = [
        'content',
        'feedback',
        'users',
        'inviteKeys',
        'logs',
        'settings',
    ]
    for (const section of sections) {
        if (canAccessSection(userRole, section)) {
            return section
        }
    }
    return null
}
